import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Calendar as CalendarIcon, Users, Moon, MapPin, Lock,
  ShieldCheck, CheckCircle2, Clock, Headphones,
} from "lucide-react";
import { toast } from "sonner";
import { fetchProperty, createCheckout, fetchPricingTiers } from "../lib/api";
import { DEFAULT_TIERS, calculateQuote } from "../lib/pricing";

function nightsBetween(a, b) {
  if (!a || !b) return 0;
  return Math.max(0, Math.round((new Date(b) - new Date(a)) / 86400000));
}

function formatLongDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

function formatWeekday(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { weekday: "long" });
  } catch { return ""; }
}

export default function BookingCheckout() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [tiers, setTiers] = useState(DEFAULT_TIERS);

  const check_in = searchParams.get("check_in") || "";
  const check_out = searchParams.get("check_out") || "";
  const initialGuests = parseInt(searchParams.get("guests") || "2", 10);

  const [form, setForm] = useState({
    full_name: "", email: "", phone: "",
    guests: Number.isFinite(initialGuests) && initialGuests > 0 ? initialGuests : 2,
    message: "",
  });

  useEffect(() => {
    fetchProperty(id)
      .then((p) => {
        setProperty(p);
        if (!p?.bookable) {
          toast.error("This residence is not available for direct online booking.");
          navigate(`/properties/${id}`, { replace: true });
        }
      })
      .catch(() => {
        toast.error("Could not load residence.");
        navigate(`/properties/${id}`, { replace: true });
      });
    fetchPricingTiers().then(setTiers).catch(() => setTiers(DEFAULT_TIERS));
  }, [id, navigate]);

  useEffect(() => {
    if (!check_in || !check_out) {
      toast.error("Please select your dates first.");
      navigate(`/properties/${id}`, { replace: true });
    }
  }, [check_in, check_out, id, navigate]);

  const nights = useMemo(() => nightsBetween(check_in, check_out), [check_in, check_out]);
  const quote = useMemo(
    () => (property ? calculateQuote(tiers, property.price_per_night, nights) : null),
    [tiers, property, nights],
  );
  const total = quote ? quote.total : 0;

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onPay = async (e) => {
    e.preventDefault();
    if (!check_in || !check_out) { toast.error("Please select your dates."); return; }
    if (nights < 1) { toast.error("Check-out must be after check-in."); return; }
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Please complete your contact details.");
      return;
    }
    setSubmitting(true);
    try {
      const phone = form.phone.trim();
      const normalisedPhone = phone.startsWith("+") ? phone : `+44 ${phone}`;
      const { url } = await createCheckout({
        property_id: property.id,
        full_name: form.full_name, email: form.email, phone: normalisedPhone,
        check_in, check_out,
        guests: parseInt(form.guests, 10) || 1,
        message: form.message,
        origin_url: window.location.origin,
      });
      window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
      setSubmitting(false);
    }
  };

  if (!property) {
    return (
      <div className="pt-32 pb-24 container mx-auto px-6 text-white/50" data-testid="booking-checkout-loading">
        Loading reservation…
      </div>
    );
  }

  const heroImage = property.images?.[0];
  const heroSrc = heroImage
    ? (heroImage.startsWith("http") ? heroImage : `${process.env.REACT_APP_BACKEND_URL || ""}${heroImage}`)
    : "";

  const maxGuests = property.guests || 6;
  const guestOptions = Array.from({ length: maxGuests }, (_, i) => i + 1);

  const payLabel = nights > 0 ? `PAY £${total.toLocaleString()} SECURELY` : "SELECT YOUR DATES";

  return (
    <div data-testid="booking-checkout-page" className="checkout-shell text-white pt-28 pb-20 -mb-24">
      <div className="container mx-auto px-6 md:px-10">
        <Link
          to={`/properties/${id}`}
          data-testid="checkout-back-link"
          className="inline-flex items-center gap-2 text-sm mb-10 transition-colors"
          style={{ color: "var(--co-gold)" }}
        >
          <ArrowLeft size={14} /> Back to property
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* LEFT — Form */}
          <div className="lg:col-span-7 space-y-8">
            <header>
              <h1 className="font-serif text-4xl md:text-5xl leading-tight text-white mb-4" data-testid="checkout-title">
                Complete your reservation
              </h1>
              <p className="text-white/70 text-base mb-4">
                Secure your stay directly with West London Stays.
              </p>
              <p className="inline-flex items-center gap-2 text-white/60 text-sm">
                <Lock size={14} style={{ color: "var(--co-gold)" }} />
                Secure online payment powered by Stripe
              </p>
            </header>

            {/* Your Stay card */}
            <section data-testid="checkout-your-stay" className="co-card p-6 md:p-7">
              <div className="co-section-heading mb-5">
                <span>Your Stay</span>
                <span className="co-underline" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div>
                  <div className="co-label flex items-center gap-2">
                    <CalendarIcon size={12} style={{ color: "var(--co-gold)" }} /> Check-in
                  </div>
                  <div className="font-serif text-2xl text-white mt-1" data-testid="checkout-checkin">
                    {formatLongDate(check_in)}
                  </div>
                  <div className="text-white/50 text-sm mt-0.5">{formatWeekday(check_in)}</div>
                </div>
                <div>
                  <div className="co-label flex items-center gap-2">
                    <CalendarIcon size={12} style={{ color: "var(--co-gold)" }} /> Check-out
                  </div>
                  <div className="font-serif text-2xl text-white mt-1" data-testid="checkout-checkout">
                    {formatLongDate(check_out)}
                  </div>
                  <div className="text-white/50 text-sm mt-0.5">{formatWeekday(check_out)}</div>
                </div>
                <div>
                  <div className="co-label flex items-center gap-2">
                    <Users size={12} style={{ color: "var(--co-gold)" }} /> Guests
                  </div>
                  <div className="font-serif text-2xl text-white mt-1" data-testid="checkout-guests-display">
                    {form.guests} {form.guests === 1 ? "guest" : "guests"}
                  </div>
                  <div className="text-white/50 text-sm mt-0.5">{nights} {nights === 1 ? "night" : "nights"}</div>
                </div>
              </div>
            </section>

            {/* Contact form */}
            <form onSubmit={onPay} data-testid="checkout-form" className="co-card p-6 md:p-7 space-y-6">
              <div>
                <label className="co-label mb-2 block" htmlFor="full_name">Full Name</label>
                <input
                  data-testid="checkout-name"
                  id="full_name" name="full_name" required
                  value={form.full_name} onChange={onChange}
                  placeholder="Enter your full name"
                  className="co-input"
                  autoComplete="name"
                />
              </div>

              <div>
                <label className="co-label mb-2 block" htmlFor="email">Email Address</label>
                <input
                  data-testid="checkout-email"
                  id="email" name="email" type="email" required
                  value={form.email} onChange={onChange}
                  placeholder="Enter your email address"
                  className="co-input"
                  autoComplete="email"
                />
              </div>

              <div>
                <label className="co-label mb-2 block" htmlFor="phone">Phone Number</label>
                <div className="co-phone-row">
                  <span className="co-phone-prefix" aria-hidden="true">
                    <span className="co-flag">🇬🇧</span>
                    <span>+44</span>
                  </span>
                  <input
                    data-testid="checkout-phone"
                    id="phone" name="phone" required
                    value={form.phone} onChange={onChange}
                    placeholder="Enter your phone number"
                    className="co-input co-input--phone"
                    autoComplete="tel"
                    inputMode="tel"
                  />
                </div>
              </div>

              <div>
                <label className="co-label mb-2 block" htmlFor="guests">Guests</label>
                <div className="co-select-wrap">
                  <select
                    data-testid="checkout-guests"
                    id="guests" name="guests"
                    value={form.guests} onChange={onChange}
                    className="co-input co-select"
                  >
                    {guestOptions.map((g) => (
                      <option key={g} value={g}>{g} {g === 1 ? "guest" : "guests"}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="co-label mb-2 block" htmlFor="message">Special Requests (optional)</label>
                <textarea
                  data-testid="checkout-message"
                  id="message" name="message" rows="4"
                  value={form.message} onChange={onChange}
                  placeholder="Any special requests or notes..."
                  className="co-input resize-none"
                />
              </div>

              {/* Safety notice */}
              <div className="co-safety" data-testid="checkout-safety">
                <div className="co-safety-icon">
                  <ShieldCheck size={20} />
                </div>
                <div className="flex-1">
                  <div className="text-white text-sm font-semibold mb-1">Your safety is our priority</div>
                  <div className="text-white/60 text-xs leading-relaxed">
                    All payments are encrypted and processed securely by Stripe.
                    We never store your card details.
                  </div>
                </div>
                <div className="co-stripe-mark" aria-hidden="true">stripe</div>
              </div>

              {/* Mobile Pay button — hidden on desktop where sticky sidebar has it */}
              <button
                type="submit"
                data-testid="checkout-pay-btn-mobile"
                disabled={submitting || nights < 1}
                className="co-pay-btn lg:hidden w-full"
              >
                {submitting ? "Redirecting…" : payLabel}
              </button>
            </form>
          </div>

          {/* RIGHT — Sticky Summary */}
          <aside className="lg:col-span-5">
            <div className="co-card p-6 md:p-7 lg:sticky lg:top-28" data-testid="checkout-summary">
              <div className="co-section-heading mb-5">
                <span>Your Booking</span>
                <span className="co-underline" />
              </div>

              {heroSrc ? (
                <div className="co-hero mb-5">
                  <img src={heroSrc} alt={property.name} loading="lazy" />
                </div>
              ) : null}

              <h2 className="font-serif text-2xl text-white leading-tight" data-testid="checkout-property-name">
                {property.name}
              </h2>
              <p className="flex items-center gap-1.5 text-white/60 text-sm mt-2 mb-5">
                <MapPin size={13} style={{ color: "var(--co-gold)" }} /> {property.location}
              </p>

              <div className="co-summary-rows">
                <div className="co-summary-row">
                  <span className="flex items-center gap-2 text-white/70">
                    <CalendarIcon size={14} style={{ color: "var(--co-gold)" }} /> Check-in
                  </span>
                  <span className="text-white">{formatLongDate(check_in)}</span>
                </div>
                <div className="co-summary-row">
                  <span className="flex items-center gap-2 text-white/70">
                    <CalendarIcon size={14} style={{ color: "var(--co-gold)" }} /> Check-out
                  </span>
                  <span className="text-white">{formatLongDate(check_out)}</span>
                </div>
                <div className="co-summary-row">
                  <span className="flex items-center gap-2 text-white/70">
                    <Users size={14} style={{ color: "var(--co-gold)" }} /> Guests
                  </span>
                  <span className="text-white">{form.guests} {form.guests === 1 ? "guest" : "guests"}</span>
                </div>
                <div className="co-summary-row">
                  <span className="flex items-center gap-2 text-white/70">
                    <Moon size={14} style={{ color: "var(--co-gold)" }} /> Nights
                  </span>
                  <span className="text-white">{nights} {nights === 1 ? "night" : "nights"}</span>
                </div>
              </div>

              <div className="co-total-row" data-testid="checkout-total-row">
                <span className="co-label" style={{ color: "var(--co-gold)" }}>Total</span>
                <span className="co-total-amount font-serif" data-testid="checkout-total">£{total.toLocaleString()}</span>
              </div>

              <p className="inline-flex items-center gap-2 text-white/60 text-xs mt-5 mb-4">
                <Lock size={13} style={{ color: "var(--co-gold)" }} />
                Secure payment powered by Stripe
              </p>

              <button
                type="button"
                onClick={onPay}
                data-testid="checkout-pay-btn"
                disabled={submitting || nights < 1}
                className="co-pay-btn w-full"
              >
                {submitting ? "Redirecting…" : payLabel}
              </button>

              <p className="flex items-center justify-center gap-2 text-white/50 text-xs mt-3">
                <CheckCircle2 size={13} className="text-emerald-500" />
                You won't be charged until you confirm payment
              </p>

              <div className="co-benefits mt-6">
                <div className="co-benefit"><CheckCircle2 size={16} className="co-benefit-icon" /><span>Secure online payment</span></div>
                <div className="co-benefit"><CheckCircle2 size={16} className="co-benefit-icon" /><span>Instant booking confirmation</span></div>
                <div className="co-benefit"><CheckCircle2 size={16} className="co-benefit-icon" /><span>Direct booking with West London Stays</span></div>
              </div>
            </div>
          </aside>
        </div>

        {/* Trust strip */}
        <div className="mt-16 pt-10 border-t border-white/10 grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="checkout-trust-strip">
          <TrustItem icon={<ShieldCheck size={22} />} title="Secure Payment"
            body="Your payment is encrypted and processed securely by Stripe." />
          <TrustItem icon={<Clock size={22} />} title="Instant Confirmation"
            body="You'll receive an instant confirmation by email after booking." />
          <TrustItem icon={<Headphones size={22} />} title="Need Help?"
            body="Our concierge team is here for you. Contact us anytime." />
        </div>
      </div>
    </div>
  );
}

function TrustItem({ icon, title, body }) {
  return (
    <div className="text-center px-4">
      <div className="mx-auto w-11 h-11 rounded-full border flex items-center justify-center mb-3"
        style={{ borderColor: "var(--co-gold)", color: "var(--co-gold)" }}>
        {icon}
      </div>
      <div className="text-white text-sm font-semibold tracking-[0.18em] uppercase mb-2">{title}</div>
      <p className="text-white/60 text-sm leading-relaxed max-w-[280px] mx-auto">{body}</p>
    </div>
  );
}
